import React, { useState, useEffect, useRef } from 'react';
import { TimeInput } from '@/components/ui/date-input';
import { Globe, Plus, Pencil, Trash2, Clock, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { COUNTRIES, statesForCountry } from '@/lib/marketplace-geo';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Countries with no public postal/pincode system — hide the pincode field.
const NO_POSTAL_CODES = new Set(['AE', 'QA']);
const countryMeta = (name) => COUNTRIES.find((c) => c.name === name || c.code === name);
const countryHasPostal = (name) => {
  const c = countryMeta(name);
  return !c || !NO_POSTAL_CODES.has(c.code);
};

const EMPTY_ZONE = { name: '', country: '', regions: '', cities: '', pincode_prefixes: '', transit_days_min: 1, transit_days_max: 3, is_default_for_country: false };

export default function DeliveryZonesPage() {
  const [settings, setSettings] = useState(null);
  const [zones, setZones] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [zoneDialog, setZoneDialog] = useState({ open: false, id: null, form: EMPTY_ZONE, saving: false });

  const load = async () => {
    try {
      const [s, z] = await Promise.all([
        axios.get(`${API}/delivery/settings`, auth()),
        axios.get(`${API}/delivery/zones`, auth()),
      ]);
      setSettings(s.data);
      setZones(z.data.zones || []);
    } catch { toast.error('Failed to load delivery settings'); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/delivery/settings`, {
        default_processing_days: parseInt(settings.default_processing_days) || 0,
        default_transit_min: parseInt(settings.default_transit_min) || 0,
        default_transit_max: parseInt(settings.default_transit_max) || 0,
        cutoff_time: settings.cutoff_time || '14:00',
        timezone_offset_hours: parseFloat(settings.timezone_offset_hours) || 0,
        show_on_tracking: !!settings.show_on_tracking,
      }, auth());
      toast.success('Delivery settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSavingSettings(false);
  };

  const openZone = (z) => setZoneDialog({
    open: true, id: z?.id || null, saving: false,
    form: z ? { ...z, regions: (z.regions || []).join(', '), cities: (z.cities || []).join(', '), pincode_prefixes: (z.pincode_prefixes || []).join(', ') } : { ...EMPTY_ZONE },
  });

  const saveZone = async () => {
    const f = zoneDialog.form;
    if (!f.name.trim() || !f.country.trim()) { toast.error('Name and country are required'); return; }
    setZoneDialog(p => ({ ...p, saving: true }));
    const body = {
      name: f.name.trim(), country: f.country.trim(),
      regions: f.regions.split(',').map(x => x.trim()).filter(Boolean),
      cities: f.cities.split(',').map(x => x.trim()).filter(Boolean),
      pincode_prefixes: countryHasPostal(f.country) ? String(f.pincode_prefixes).split(',').map(x => x.trim()).filter(Boolean) : [],
      transit_days_min: parseInt(f.transit_days_min) || 0,
      transit_days_max: parseInt(f.transit_days_max) || 0,
      is_default_for_country: !!f.is_default_for_country,
    };
    try {
      if (zoneDialog.id) await axios.put(`${API}/delivery/zones/${zoneDialog.id}`, body, auth());
      else await axios.post(`${API}/delivery/zones`, body, auth());
      toast.success('Zone saved');
      setZoneDialog({ open: false, id: null, form: EMPTY_ZONE, saving: false });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zone save failed');
      setZoneDialog(p => ({ ...p, saving: false }));
    }
  };

  const deleteZone = async (id) => {
    try { await axios.delete(`${API}/delivery/zones/${id}`, auth()); toast.success('Zone deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const setF = (k, v) => setZoneDialog(p => ({ ...p, form: { ...p.form, [k]: v } }));

  const onCountryChange = (name) => setZoneDialog(p => ({
    ...p,
    form: {
      ...p.form,
      country: name === 'Other' ? '' : name,
      _countryChoice: name,
      regions: '',
      cities: '',
      pincode_prefixes: countryHasPostal(name) ? p.form.pincode_prefixes : '',
    },
  }));

  const toggleRegion = (r) => {
    const cur = String(zoneDialog.form.regions || '').split(',').map(x => x.trim()).filter(Boolean);
    const next = cur.includes(r) ? cur.filter(x => x !== r) : [...cur, r];
    setF('regions', next.join(', '));
  };

  const fileRef = useRef(null);
  const onPincodeFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false });
      const found = [];
      for (const row of rows) {
        for (const cell of (row || [])) {
          const v = String(cell ?? '').trim();
          if (/^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/.test(v) && /\d/.test(v)) found.push(v);
        }
      }
      if (!found.length) { toast.error('No pincodes found in the file'); return; }
      const existing = String(zoneDialog.form.pincode_prefixes || '').split(',').map(x => x.trim()).filter(Boolean);
      const merged = [...new Set([...existing, ...found])];
      setF('pincode_prefixes', merged.join(', '));
      toast.success(`Imported ${found.length} pincodes (${merged.length} total after de-duplication)`);
    } catch { toast.error('Could not read the file — use .xlsx, .xls or .csv'); }
  };

  if (!settings) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-wt-gold" /></div>;

  return (
    <div className="space-y-6" data-testid="delivery-zones-page">
      <div>
        <h1 className="text-2xl font-bold text-wt-text-primary">Delivery ETA</h1>
        <p className="text-wt-text-muted">Zones, cut-off time and processing defaults — powers the estimated delivery date customers see on tracking</p>
      </div>

      <Card className="bg-wt-surface border-wt-border">
        <CardHeader><CardTitle className="text-wt-text-primary flex items-center gap-2"><Clock className="h-5 w-5 text-wt-gold" />Defaults & Cut-off</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-end">
          <div>
            <Label className="text-wt-text-primary text-xs">Processing days (default)</Label>
            <Input type="number" min="0" value={settings.default_processing_days} onChange={(e) => setSettings({ ...settings, default_processing_days: e.target.value })} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="default-processing-days" />
          </div>
          <div>
            <Label className="text-wt-text-primary text-xs">Transit min (default)</Label>
            <Input type="number" min="0" value={settings.default_transit_min} onChange={(e) => setSettings({ ...settings, default_transit_min: e.target.value })} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="default-transit-min" />
          </div>
          <div>
            <Label className="text-wt-text-primary text-xs">Transit max (default)</Label>
            <Input type="number" min="0" value={settings.default_transit_max} onChange={(e) => setSettings({ ...settings, default_transit_max: e.target.value })} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="default-transit-max" />
          </div>
          <div>
            <Label className="text-wt-text-primary text-xs">Cut-off time (local)</Label>
            <TimeInput  value={settings.cutoff_time} onChange={(e) => setSettings({ ...settings, cutoff_time: e.target.value })} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="cutoff-time-input" />
          </div>
          <div>
            <Label className="text-wt-text-primary text-xs">Timezone offset (hrs)</Label>
            <Input type="number" step="0.5" value={settings.timezone_offset_hours} onChange={(e) => setSettings({ ...settings, timezone_offset_hours: e.target.value })} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="tz-offset-input" />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch checked={!!settings.show_on_tracking} onCheckedChange={(v) => setSettings({ ...settings, show_on_tracking: v })} data-testid="show-on-tracking-toggle" />
            <Label className="text-wt-text-primary text-xs">Show on tracking</Label>
          </div>
        </CardContent>
        <CardContent className="pt-0 flex justify-between items-center">
          <p className="text-xs text-wt-text-muted">Orders placed after the cut-off time count from the next day. Estimated delivery = readiness (processing / production days) + zone transit range.</p>
          <Button onClick={saveSettings} disabled={savingSettings} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="save-delivery-settings">{savingSettings ? 'Saving…' : 'Save Settings'}</Button>
        </CardContent>
      </Card>

      <Card className="bg-wt-surface border-wt-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-wt-text-primary flex items-center gap-2"><Globe className="h-5 w-5 text-wt-gold" />Delivery Zones ({zones.length})</CardTitle>
          <Button onClick={() => openZone(null)} className="bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="add-zone-btn"><Plus className="h-4 w-4 mr-1" />Add Zone</Button>
        </CardHeader>
        <CardContent>
          {zones.length === 0 ? (
            <p className="text-center text-wt-text-muted py-6">No zones yet — orders will use the default transit days. Add zones like "Dubai (1 day)" or "Rest of Country".</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-wt-border text-wt-text-muted text-left">
                  <th className="p-2">Zone</th><th className="p-2">Country</th><th className="p-2">Regions</th><th className="p-2">Cities</th><th className="p-2">Pincode prefixes</th><th className="p-2 text-center">Transit</th><th className="p-2 text-center">Country default</th><th className="p-2"></th>
                </tr></thead>
                <tbody>
                  {zones.map(z => (
                    <tr key={z.id} className="border-b border-wt-border/40" data-testid={`zone-row-${z.id}`}>
                      <td className="p-2 text-wt-text-primary font-medium">{z.name}</td>
                      <td className="p-2 text-wt-text-secondary">{z.country}</td>
                      <td className="p-2 text-wt-text-muted">{(z.regions || []).join(', ') || '—'}</td>
                      <td className="p-2 text-wt-text-muted">{(z.cities || []).join(', ') || '—'}</td>
                      <td className="p-2 text-wt-text-muted">{(z.pincode_prefixes || []).join(', ') || '—'}</td>
                      <td className="p-2 text-center text-wt-text-primary">{z.transit_days_min}–{z.transit_days_max}d</td>
                      <td className="p-2 text-center">{z.is_default_for_country ? '✓' : ''}</td>
                      <td className="p-2 text-right whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => openZone(z)} data-testid={`edit-zone-${z.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => deleteZone(z.id)} data-testid={`delete-zone-${z.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={zoneDialog.open} onOpenChange={(o) => !o && setZoneDialog(p => ({ ...p, open: false }))}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg" data-testid="zone-dialog">
          <DialogHeader><DialogTitle className="text-wt-text-primary">{zoneDialog.id ? 'Edit Zone' : 'Add Zone'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(() => {
              const f = zoneDialog.form;
              const choice = f._countryChoice || (f.country ? (countryMeta(f.country)?.name || 'Other') : '');
              const states = choice && choice !== 'Other' ? statesForCountry(choice) : [];
              const selectedRegions = String(f.regions || '').split(',').map(x => x.trim()).filter(Boolean);
              const hasPostal = !choice || countryHasPostal(choice);
              return (<>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-wt-text-primary">Zone name *</Label><Input value={f.name} onChange={(e) => setF('name', e.target.value)} placeholder="e.g. Dubai" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-name-input" /></div>
              <div>
                <Label className="text-wt-text-primary">Country *</Label>
                <Select value={choice} onValueChange={onCountryChange}>
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border max-h-64">
                    {COUNTRIES.map(c => <SelectItem key={c.code} value={c.name} data-testid={`zone-country-opt-${c.code}`}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {choice === 'Other' && (
              <div><Label className="text-wt-text-primary">Country name *</Label><Input value={f.country} onChange={(e) => setF('country', e.target.value)} placeholder="Type country name" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-country-input" /></div>
            )}
            {choice && (states.length > 0 ? (
              <div>
                <Label className="text-wt-text-primary">Regions / states <span className="text-wt-text-muted font-normal">(select — optional)</span></Label>
                <div className="mt-1 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-2 rounded-md border border-wt-border bg-wt-bg" data-testid="zone-regions-picker">
                  {states.map(s => (
                    <button key={s} type="button" onClick={() => toggleRegion(s)}
                      className={`px-2 py-1 rounded-full text-xs border transition-colors ${selectedRegions.includes(s) ? 'bg-wt-gold text-black border-wt-gold' : 'bg-transparent text-wt-text-secondary border-wt-border hover:border-wt-gold/60'}`}
                      data-testid={`zone-region-chip-${s.replace(/\s+/g, '-')}`}>{s}</button>
                  ))}
                </div>
              </div>
            ) : (
              <div><Label className="text-wt-text-primary">Regions / states <span className="text-wt-text-muted font-normal">(comma-separated, optional)</span></Label><Input value={f.regions} onChange={(e) => setF('regions', e.target.value)} placeholder="Dubai, Sharjah" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-regions-input" /></div>
            ))}
            {choice && <div><Label className="text-wt-text-primary">Cities <span className="text-wt-text-muted font-normal">(comma-separated, optional)</span></Label><Input value={f.cities} onChange={(e) => setF('cities', e.target.value)} placeholder="Dubai, Al Ain" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-cities-input" /></div>}
            {choice && hasPostal && (
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-wt-text-primary">Pincode / ZIP prefixes <span className="text-wt-text-muted font-normal">(optional — most specific match wins)</span></Label>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-wt-border text-wt-text-secondary" onClick={() => fileRef.current?.click()} data-testid="zone-pincode-upload-btn"><Upload className="h-3 w-3 mr-1" />Upload Excel</Button>
                  <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onPincodeFile} data-testid="zone-pincode-file-input" />
                </div>
                <Input value={f.pincode_prefixes} onChange={(e) => setF('pincode_prefixes', e.target.value)} placeholder="400, 4001" className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-pincodes-input" />
                <p className="text-[11px] text-wt-text-muted mt-1">Upload a .xlsx / .csv — all pincodes found in the file are imported and de-duplicated.</p>
              </div>
            )}
            {choice && !hasPostal && (
              <p className="text-[11px] text-wt-text-muted" data-testid="zone-no-postal-note">{choice} has no postal-code system — zones match by region / city instead.</p>
            )}</>);
            })()}
            <div className="grid grid-cols-3 gap-3 items-end">
              <div><Label className="text-wt-text-primary">Transit min (days)</Label><Input type="number" min="0" value={zoneDialog.form.transit_days_min} onChange={(e) => setF('transit_days_min', e.target.value)} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-transit-min" /></div>
              <div><Label className="text-wt-text-primary">Transit max (days)</Label><Input type="number" min="0" value={zoneDialog.form.transit_days_max} onChange={(e) => setF('transit_days_max', e.target.value)} className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary" data-testid="zone-transit-max" /></div>
              <div className="flex items-center gap-2 pb-2"><Switch checked={!!zoneDialog.form.is_default_for_country} onCheckedChange={(v) => setF('is_default_for_country', v)} data-testid="zone-default-toggle" /><Label className="text-wt-text-primary text-xs">Default for country</Label></div>
            </div>
            <Button onClick={saveZone} disabled={zoneDialog.saving} className="w-full bg-wt-gold hover:bg-wt-gold-hover text-black" data-testid="zone-save-btn">{zoneDialog.saving ? 'Saving…' : 'Save Zone'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
